import { useCallback, useEffect, useId, useMemo, type RefObject } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { Switch } from '@gokrew/ui/components/Switch';
import { ManagesTeamBlock } from '@/components/reporting/ManagesTeamBlock';
import { useManageTeamPlan } from '@/hooks/reporting/useManageTeam';
import type { AddEmployeeFormValues } from '@/types/employee';
import {
  emptyManageTeamSelection,
  type ManageTeamPlan,
  type ManageTeamSelection,
} from '@/utils/manageTeam';
import styles from './ManagesTeamSection.module.css';

interface ManagesTeamSectionProps {
  /** Receives the plan to apply once the employee is saved. */
  planRef: RefObject<ManageTeamPlan | null>;
}

/** The new hire's display name, or "this person" before it is typed. */
const useNewHireName = () => {
  const { t } = useTranslation(TranslationNS.Employee);
  const { values } = useFormikContext<AddEmployeeFormValues>();
  const name = `${values.user?.firstName ?? ''} ${values.user?.lastName ?? ''}`
    .replace(/\s+/g, ' ')
    .trim();
  return name || t('reporting_line.manages_team.this_person');
};

/**
 * "Manages a team?" on the Add employee form (only rendered for users with
 * `reporting.update`): No by default and nothing else shows; Yes opens the
 * "Who will report to {name}?" block. The plan (with the new hire's own
 * "Reports to") is handed to the form, which applies it after the save.
 */
export const ManagesTeamSection = ({ planRef }: ManagesTeamSectionProps) => {
  const { t } = useTranslation(TranslationNS.Employee);
  const switchId = useId();
  const { values, setFieldValue } = useFormikContext<AddEmployeeFormValues>();
  const name = useNewHireName();
  const managesTeam = !!values.managesTeam;
  const selection = useMemo(
    () =>
      managesTeam
        ? (values.teamSelection ?? emptyManageTeamSelection())
        : emptyManageTeamSelection(),
    [managesTeam, values.teamSelection]
  );
  const reportsTo = values.reportsTo ?? null;

  const { data, plan, isLoading, canRead } = useManageTeamPlan({
    enabled: managesTeam,
    managerId: null,
    selection,
    reportsTo,
  });

  useEffect(() => {
    planRef.current = plan;
  }, [plan, planRef]);

  const onChange = useCallback(
    (next: ManageTeamSelection) => setFieldValue('teamSelection', next),
    [setFieldValue]
  );
  const excludeIds = useMemo(
    () => (reportsTo ? [reportsTo.employeeId] : []),
    [reportsTo]
  );

  return (
    <section className={styles.section}>
      <div className={styles.toggleRow}>
        <label htmlFor={switchId} className={styles.toggleLabel}>
          {t('reporting_line.manages_team.toggle')}
        </label>
        <span className={styles.toggleValue}>
          {managesTeam
            ? t('reporting_line.manages_team.yes')
            : t('reporting_line.manages_team.no')}
          <Switch
            id={switchId}
            size="sm"
            checked={managesTeam}
            onCheckedChange={(checked) => setFieldValue('managesTeam', checked)}
          />
        </span>
      </div>
      {managesTeam && (
        <ManagesTeamBlock
          managerId={null}
          managerName={name}
          selection={selection}
          onChange={onChange}
          data={data}
          plan={plan}
          isLoading={isLoading}
          canRead={canRead}
          excludeIds={excludeIds}
        />
      )}
    </section>
  );
};
